import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import {
  LayoutDashboard,
  Building2,
  UserCog,
  CreditCard,
  Puzzle,
  UserCheck,
  Settings,
  Link,
  Loader2,
  LucideIcon,
} from "lucide-react";
import useToken from "../../../../hooks/features/auth/useToken";
import useAuth from "../../../../hooks/features/auth/useAuth";
import {useModules} from "../../../../hooks/features/modules/useModules";

interface SidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
}

interface MenuItem {
  icon: LucideIcon;
  label: string;
  route: string;
  category: "admin" | "tools" | "management";
  roles: string[];
}

// Récupérer dynamiquement une icône depuis lucide-react
const getIcon = (iconName: string): LucideIcon => {
  if (iconName && LucideIcons[iconName as keyof typeof LucideIcons]) {
    return LucideIcons[iconName as keyof typeof LucideIcons] as LucideIcon;
  }
  return LayoutDashboard;
};

export const Sidebar: React.FC<SidebarProps> = ({ isCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { entrepriseModules, getEntrepriseModules, loading } = useModules();

  const userRole = user?.role || "agent";



  // Modules actifs de l'entreprise
  const activeModules = entrepriseModules.filter(m => m.is_enabled && m.is_active);
  const hasActiveModules = activeModules.length > 0;

  // Construction des items du menu
  const menuItems: MenuItem[] = [
    // ============================================
    // ADMINISTRATION (admin uniquement)
    // ============================================
    ...(userRole === 'admin' ? [
      { icon: LayoutDashboard, label: "Tableau de bord", route: "/dashboard", category: "admin" as const, roles: ["admin"] },
      { icon: Building2, label: "Les entreprises", route: "/entreprises", category: "admin" as const, roles: ["admin"] },
      { icon: UserCog, label: "Les utilisateurs", route: "/users", category: "admin" as const, roles: ["admin"] },
      { icon: CreditCard, label: "Les abonnements", route: "/subscriptions", category: "admin" as const, roles: ["admin"] },
      { icon: Puzzle, label: "Modules", route: "/modules", category: "admin" as const, roles: ["admin"] },
      { icon: LucideIcons.Megaphone, label: "Annonces", route: "/annonces", category: "admin" as const, roles: ["admin"] },
    ] : []),

    // ============================================
    // VOS OUTILS (modules actifs)
    // ============================================
    ...(userRole !== 'admin' ? activeModules.map((module) => {
      const IconComponent = getIcon(module.icon);
      return {
        icon: IconComponent,
        label: module.name,
        route: `/${module.key}`,
        category: "tools" as const,
        roles: ["entreprise", "agent"],
      };
    }) : []),

    // ============================================
    // GESTION (toujours présents)
    // ============================================
    ...(userRole !== 'admin' ? [
      { icon: UserCheck, label: "Agents", route: "/agents", category: "management" as const, roles: ["entreprise"] },
      { icon: Settings, label: "Paramètres", route: "/settings", category: "management" as const, roles: ["entreprise"] },
      { icon: Link, label: "Intégrations", route: "/integrations", category: "management" as const, roles: ["entreprise"] },
    ] : []),
  ];

  const hasAccess = (item: MenuItem) => item.roles.includes(userRole);
  const filteredItems = menuItems.filter(hasAccess);

  const handleNavigation = (item: MenuItem) => {
    navigate(`/${userRole}${item.route}`);
  };

  const isActiveRoute = (route: string) => location.pathname.includes(route);

  const adminItems = filteredItems.filter(item => item.category === "admin");
  const toolsItems = filteredItems.filter(item => item.category === "tools");
  const managementItems = filteredItems.filter(item => item.category === "management");

  // Afficher un loader pendant le chargement
  if (loading && userRole !== "admin" && entrepriseModules.length === 0) {
    return (
      <div className={`bg-[#031B4E] ${isCollapsed ? "w-20" : "w-64"} transition-all duration-300 h-full flex flex-col items-center justify-center text-slate-300`}>
        <Loader2 className="w-6 h-6 animate-spin" />
        {!isCollapsed && <span className="text-xs mt-2">Chargement...</span>}
      </div>
    );
  }

  return (
    <div className={`bg-[#031B4E] ${isCollapsed ? "w-20" : "w-64"} transition-all duration-300 h-full flex flex-col select-none text-slate-300`}>
      {/* Header / Brand */}
      <div className={`p-6 flex items-center ${isCollapsed ? "justify-center" : "space-x-3"}`}>
        <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center shadow-lg shadow-primary/30 flex-shrink-0">
          <LayoutDashboard className="w-4 h-4 text-white" />
        </div>
        {!isCollapsed && (
          <div className="flex items-center space-x-1.5">
            <span className="font-bold text-white tracking-wide text-md uppercase">Izi</span>
            <span className="font-bold text-iziYellow tracking-wide text-md uppercase">Call Center</span>
          </div>
        )}
      </div>

      {/* Navigation Links */}
      <nav className="flex-1 px-4 py-2 overflow-y-auto space-y-1 custom-scrollbar">
        <ul className="space-y-1">
          {/* Section Administration */}
          {adminItems.length > 0 && (
            <>
              {!isCollapsed && (
                <li className="px-3 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    Administration
                  </p>
                </li>
              )}
              {adminItems.map((item, index) => {
                const Icon = item.icon;
                const isActive = isActiveRoute(item.route);

                return (
                  <li key={`admin-${index}`}>
                    <button
                      onClick={() => handleNavigation(item)}
                      title={isCollapsed ? item.label : undefined}
                      className={`w-full flex items-center rounded-xl transition-all duration-150 group ${isCollapsed ? "justify-center p-3" : "px-4 py-2.5 justify-between"
                        } ${isActive
                          ? "text-white bg-primary font-medium shadow-md shadow-primary/10"
                          : "text-slate-400 hover:bg-white/5 hover:text-white"
                        }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-white" : "text-slate-400 group-hover:text-white"}`} />
                        {!isCollapsed && <span className="text-[13.5px] tracking-wide">{item.label}</span>}
                      </div>
                    </button>
                  </li>
                );
              })}
            </>
          )}

          {/* Séparateur après Admin */}
          {adminItems.length > 0 && (toolsItems.length > 0 || managementItems.length > 0) && !isCollapsed && (
            <li className="px-3 py-2">
              <div className="border-t border-slate-700/50"></div>
            </li>
          )}

          {/* Section Vos outils */}
          {toolsItems.length > 0 && (
            <>
              {!isCollapsed && (
                <li className="px-3 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    Vos outils
                  </p>
                </li>
              )}
              {toolsItems.map((item, index) => {
                const Icon = item.icon;
                const isActive = isActiveRoute(item.route);

                return (
                  <li key={`tools-${index}`}>
                    <button
                      onClick={() => handleNavigation(item)}
                      title={isCollapsed ? item.label : undefined}
                      className={`w-full flex items-center rounded-xl transition-all duration-150 group ${isCollapsed ? "justify-center p-3" : "px-4 py-2.5 justify-between"
                        } ${isActive
                          ? "text-white bg-primary font-medium shadow-md shadow-primary/10"
                          : "text-slate-400 hover:bg-white/5 hover:text-white"
                        }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-white" : "text-slate-400 group-hover:text-white"}`} />
                        {!isCollapsed && <span className="text-[13.5px] tracking-wide">{item.label}</span>}
                      </div>
                    </button>
                  </li>
                );
              })}
            </>
          )}

          {/* Séparateur après Vos outils */}
          {toolsItems.length > 0 && managementItems.length > 0 && !isCollapsed && (
            <li className="px-3 py-2">
              <div className="border-t border-slate-700/50"></div>
            </li>
          )}

          {/* Section Gestion */}
          {managementItems.length > 0 && (
            <>
              {!isCollapsed && (
                <li className="px-3 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    Gestion
                  </p>
                </li>
              )}
              {managementItems.map((item, index) => {
                const Icon = item.icon;
                const isActive = isActiveRoute(item.route);

                return (
                  <li key={`management-${index}`}>
                    <button
                      onClick={() => handleNavigation(item)}
                      title={isCollapsed ? item.label : undefined}
                      className={`w-full flex items-center rounded-xl transition-all duration-150 group ${isCollapsed ? "justify-center p-3" : "px-4 py-2.5 justify-between"
                        } ${isActive
                          ? "text-white bg-primary font-medium shadow-md shadow-primary/10"
                          : "text-slate-400 hover:bg-white/5 hover:text-white"
                        }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-white" : "text-slate-400 group-hover:text-white"}`} />
                        {!isCollapsed && <span className="text-[13.5px] tracking-wide">{item.label}</span>}
                      </div>
                    </button>
                  </li>
                );
              })}
            </>
          )}

          {/* Message si aucun module actif */}
          {!hasActiveModules && userRole !== "admin" && !loading && (
            <li className="px-3 py-6">
              <div className="text-center">
                <p className="text-xs text-slate-500 mb-3">
                  Aucun module actif
                </p>
                <button
                  onClick={() => navigate(`/${userRole}/settings/entreprise-module`)}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors"
                >
                  <Settings size={14} />
                  Configurer les modules
                </button>
              </div>
            </li>
          )}
        </ul>
      </nav>
    </div>
  );
};